import React, { createContext, useContext, useState, ReactNode } from 'react';

interface TutorialContextType {
  showTutorial: boolean;
  currentTutorialStep: number;
  tutorialPath: string | null;
  waitingForClick: string | null; // Menu item ID that tutorial is waiting for
  startTutorial: (step?: number, path?: string) => void;
  nextTutorialStep: () => void;
  previousTutorialStep: () => void;
  completeTutorial: () => void;
  skipTutorial: () => void;
  setWaitingForClick: (menuId: string | null) => void;
  handleMenuClick: (menuId: string) => void;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export const TutorialProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentTutorialStep, setCurrentTutorialStep] = useState(0);
  const [tutorialPath, setTutorialPath] = useState<string | null>(null);
  const [waitingForClick, setWaitingForClick] = useState<string | null>(null);

  const startTutorial = (step: number = 0, path: string | null = null) => {
    setCurrentTutorialStep(step);
    setTutorialPath(path);
    setShowTutorial(true);
    setWaitingForClick(null);
  };

  const nextTutorialStep = () => {
    setCurrentTutorialStep(prev => prev + 1);
    setWaitingForClick(null);
  };

  const previousTutorialStep = () => {
    setCurrentTutorialStep(prev => Math.max(0, prev - 1));
    setWaitingForClick(null);
  };

  const completeTutorial = () => {
    setShowTutorial(false);
    setCurrentTutorialStep(0);
    setTutorialPath(null);
    setWaitingForClick(null);
  };

  const skipTutorial = () => {
    setShowTutorial(false);
    setCurrentTutorialStep(0);
    setTutorialPath(null);
    setWaitingForClick(null);
  };

  const handleMenuClick = (menuId: string) => {
    if (showTutorial && waitingForClick === menuId) {
      // User clicked on the expected menu item
      if (menuId === 'test-rules' && currentTutorialStep === 2) {
        // Move to next step after navigation
        setTimeout(() => {
          setCurrentTutorialStep(3);
        }, 500);
      } else if (menuId === 'material' && currentTutorialStep === 3) {
        // Complete tutorial after navigation
        setTimeout(() => {
          completeTutorial();
        }, 500);
      }
    }
  };

  return (
    <TutorialContext.Provider
      value={{
        showTutorial,
        currentTutorialStep,
        tutorialPath,
        waitingForClick,
        startTutorial,
        nextTutorialStep,
        previousTutorialStep,
        completeTutorial,
        skipTutorial,
        setWaitingForClick,
        handleMenuClick,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

